import { DefaultButton, mergeStyleSets, Stack } from '@fluentui/react';
import React from 'react';
import StackTokens from '../shared/StackTokens';
import ModellerReducer from './ModellerReducer';
import Store from '../shared/state/Store';
import Styles from './Styles';
import DataOutputItem from './io/DataOutputItem';
import ModellerDefaults from './ModellerDefaults';
import Theme from '../shared/Theme';
import CardChart from './io/chart/CardChart';

const componentStyles = mergeStyleSets({
  root: {
    width: '100%',
    minWidth: `${ModellerDefaults.ComponentDefaults.RightWidthMin}px`,
  },
  container: {
    height: ModellerDefaults.ComponentDefaults.ComponentHeight,
    // background: 'lime',
    overflowY: 'auto',
    paddingRight: '5px',
    marginTop: '7px !important',
    width: '100%',
  },

  rootMainWhenMinimised: {
    position: 'absolute',
    right: '10000px',
  },

  rootMinimised: {
    width: '25px',
    height: ModellerDefaults.ComponentDefaults.ComponentHeight,
    // marginTop: '34px',
    position: 'absolute',
    background: 'white',
  },
  headerMinimised: {
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
    paddingTop: '5px',
  },
  // Hidden container for rendering charts at actual node sizes
  // Uses opacity instead of offscreen positioning for better measurement accuracy
  // Positioned at bottom-right of viewport, invisible but on-screen for accurate measurements
  hiddenChartContainer: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: '2000px',
    height: '2000px',
    overflow: 'hidden',
    pointerEvents: 'none',
    opacity: 0,
    zIndex: -1,
  },
  hiddenChartItem: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
});

export default ({ minimised, setMinimised }) => {
  const [globalState] = Store.useStore();
  const model = ModellerReducer.getModel(globalState);

  const modelViewMode = globalState.config?.generalConfig?.modelViewMode;
  const modelViewCustomPermissions = modelViewMode === 'custom' ? (globalState.config?.generalConfig?.modelViewCustomPermissions ?? {}) : {};
  const showHiddenNodes = modelViewCustomPermissions.showHiddenNodes || false;

  const minimisedSection = minimised && (
    <Stack vertical className={componentStyles.rootMinimised}>
      <div>
        <DefaultButton
          text=""
          iconProps={{
            iconName: 'ChevronLeft',
          }}
          className={[Theme.styles.outlineControlButton, Styles.sideMinimizeButton].join(' ')}
          onClick={async () => {
            setMinimised(false);
          }}
          title="Expand"
        />
      </div>
      <header className={[Styles.headerSection, componentStyles.headerMinimised].join(' ')}>Charts</header>
    </Stack>
  );

  // Track the renderChartsRequested timestamp to detect when calculations complete
  // and force re-render of all mode_chart node graphics
  const renderChartsRequested = ModellerReducer.getSetting(globalState, ModellerReducer.SettingKeys.renderChartsRequested);
  const lastRenderChartsRequestedRef = React.useRef(null);
  const renderChartsRequestedChanged = renderChartsRequested !== undefined
    && renderChartsRequested !== lastRenderChartsRequestedRef.current;
  const lastRenderedChartsRef = React.useRef({});

  const getChartRefreshKey = (chartData) => {
    const refreshedAtValues = chartData
      .map((dsData) => dsData?.refreshedAt)
      .filter((value) => Number.isFinite(value));
    if (refreshedAtValues.length === 0) {
      return 0;
    }
    return Math.max(...refreshedAtValues);
  };

  const getLastRenderedRefreshKey = (networkId, nodeId) => (
    lastRenderedChartsRef.current?.[networkId]?.[nodeId]
  );

  const setLastRenderedRefreshKey = (networkId, nodeId, refreshKey) => {
    lastRenderedChartsRef.current = {
      ...(lastRenderedChartsRef.current || {}),
      [networkId]: {
        ...(lastRenderedChartsRef.current?.[networkId] || {}),
        [nodeId]: refreshKey,
      },
    };
  };

  const handleChartRendered = React.useCallback((networkId, nodeId, refreshKey) => {
    setLastRenderedRefreshKey(networkId, nodeId, refreshKey);
  }, []);

  // Collect nodes that have result data for potential graph chart rendering
  const nodesWithData = [];
  if (model.networks) {
    model.networks.forEach((network) => {
      if (network.nodes) {
        network.nodes.forEach((node) => {
          // Check if node has result data
          const chartData = ModellerReducer
            .getModel(globalState).dataSets
            .map((dataset) => (ModellerReducer.getMappedResult(globalState, dataset.id, network.id, node.id)))
            .filter((el) => el !== undefined);

          // Only include nodes that have chart data
          if (chartData && chartData.length > 0) {
            const nodeGraphics = node.graphics || ModellerDefaults.DefaultGraphics;
            const graphGraphics = nodeGraphics.graph || ModellerDefaults.DefaultGraphics.graph;
            const graphClasses = graphGraphics.classes || [];
            const isModeChart = graphClasses.includes(`mode_${ModellerDefaults.NodeModes.CHART}`);

            // eslint-disable-next-line camelcase
            const hasChartBackground = Boolean(graphGraphics?.style?.custom?.mode_chart?.['background-image']);
            const chartRefreshKey = getChartRefreshKey(chartData);
            const hasRefreshKey = chartRefreshKey > 0;

            nodesWithData.push({
              network,
              node,
              chartData,
              isModeChart,
              hasChartBackground,
              chartRefreshKey,
              hasRefreshKey,
            });
          }
        });
      }
    });
  }

  React.useEffect(() => {
    if (renderChartsRequested !== undefined && renderChartsRequested !== lastRenderChartsRequestedRef.current) {
      // Only mark as processed if we have nodes to render, otherwise wait for next render
      // when results might be available. This ensures renderChartsRequestedChanged stays
      // true until we actually have data to render charts for.
      if (nodesWithData.length > 0) {
        lastRenderChartsRequestedRef.current = renderChartsRequested;
      }
    }
  }, [renderChartsRequested, nodesWithData.length]);

  // Graph offscreen rendering:
  // - Only nodes in mode_chart
  // - If they are missing a background OR a new calculation just finished
  // Sidebar charts remain unchanged.
  const nodesRequiringGraphCharts = nodesWithData.filter((nodeInfo) => (
    nodeInfo.isModeChart && (
      renderChartsRequestedChanged
      || !nodeInfo.hasChartBackground
      || (nodeInfo.hasRefreshKey
        && nodeInfo.chartRefreshKey !== getLastRenderedRefreshKey(nodeInfo.network.id, nodeInfo.node.id))
    )
  ));

  // Note: render logs are emitted on successful chart snapshot (onChartRendered)

  // Keep previous sort to render mode_chart nodes first when applicable
  const sortedNodesWithData = [...nodesRequiringGraphCharts].sort((a, b) => {
    const aGraphics = a.node.graphics?.graph || ModellerDefaults.DefaultGraphics.graph;
    const aClasses = aGraphics.classes || [];
    const aIsModeChart = aClasses.includes(`mode_${ModellerDefaults.NodeModes.CHART}`);

    const bGraphics = b.node.graphics?.graph || ModellerDefaults.DefaultGraphics.graph;
    const bClasses = bGraphics.classes || [];
    const bIsModeChart = bClasses.includes(`mode_${ModellerDefaults.NodeModes.CHART}`);

    if (aIsModeChart && !bIsModeChart) return -1;
    if (!aIsModeChart && bIsModeChart) return 1;
    return 0;
  });


  return (
    <div className={componentStyles.root}>
      {minimisedSection}
      {!minimised && (
        <Stack vertical tokens={StackTokens.spacing} className={minimised ? componentStyles.rootMainWhenMinimised : ''}>
          <Stack horizontal tokens={StackTokens.spacing5}>
            <DefaultButton
              text=""
              iconProps={{
                iconName: 'ChevronRight',
              }}
              className={[Theme.styles.outlineControlButton, Styles.sideMinimizeButton].join(' ')}
              onClick={async () => {
                setMinimised(true);
              }}
              title="Minimise"
            />
            <header className={Styles.headerSection}>Charts</header>
          </Stack>
          <Stack vertical tokens={StackTokens.spacing} className={componentStyles.container}>
            {
            model.networks.map((network) => (
              <React.Fragment key={network.id}>
                <header className={Styles.headerSubsection} key={network.id}>{network.name || network.id}</header>
                {
                  network.nodes.map((node) => {
                    const isHidden = node.graphics?.visible === false;
                    // Render charts for hidden nodes when showHiddenNodes is true
                    if (isHidden && !showHiddenNodes) {
                      return <React.Fragment key={node.id} />;
                    }
                    return (
                      <DataOutputItem
                        key={node.id}
                        networkId={network.id}
                        nodeConfig={node}
                      />
                    );
                  })
                }
              </React.Fragment>
            ))
          }
          </Stack>
        </Stack>
      )}

      {/* Hidden offscreen container for rendering charts at actual node sizes for graph conversion */}
      <div className={componentStyles.hiddenChartContainer}>
        {sortedNodesWithData.map(({
          network, node, chartData, chartRefreshKey,
        }) => {
          // Use the same approach as DataOutputItem - get width/height from graph
          // First try graph.width/height (numeric), then try parsing from style.width/height (CSS strings)
          const nodeGraphics = node.graphics || ModellerDefaults.DefaultGraphics;
          let width = nodeGraphics?.graph?.width;
          let height = nodeGraphics?.graph?.height;

          // If not found, try parsing from style (CSS values like "150px")
          if (!width) {
            const styleWidth = nodeGraphics?.graph?.style?.width;
            width = styleWidth ? parseFloat(styleWidth) : ModellerDefaults.DefaultGraphics.graph.width;
          }
          if (!height) {
            const styleHeight = nodeGraphics?.graph?.style?.height;
            height = styleHeight ? parseFloat(styleHeight) : ModellerDefaults.DefaultGraphics.graph.height;
          }

          // Use the full node height for the chart
          // The background-offset-y in Cytoscape positions the chart, but the chart
          // needs the full height to properly render with margins for axes
          const chartHeight = height;

          // Enforce minimum size for off-screen charts to ensure readability
          // Charts rendered at very small sizes become unreadable, so use minimum 150x100
          // But preserve the node's aspect ratio when applying minimums
          const MIN_CHART_WIDTH = 150;
          const MIN_CHART_HEIGHT = 100;

          // Calculate the scale needed to meet minimums while preserving aspect ratio
          let renderWidth = width;
          let renderHeight = chartHeight;

          if (width < MIN_CHART_WIDTH || chartHeight < MIN_CHART_HEIGHT) {
            // Calculate scale factors needed to meet each minimum
            const widthScale = MIN_CHART_WIDTH / width;
            const heightScale = MIN_CHART_HEIGHT / chartHeight;

            // Use the larger scale to ensure both minimums are met while preserving aspect ratio
            const scale = Math.max(widthScale, heightScale);
            renderWidth = width * scale;
            renderHeight = chartHeight * scale;
          }

          return (
            <div
              key={`${network.id}-${node.id}`}
              className={componentStyles.hiddenChartItem}
              style={{
                width: `${renderWidth}px`,
                height: `${renderHeight}px`,
              }}
            >
              <CardChart
                data={chartData}
                nodeConfig={node}
                networkId={network.id}
                renderOffScreen
                chartRefreshKey={chartRefreshKey}
                onChartRendered={handleChartRendered}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
